import React from "react";
import { Column, Columns, Stack } from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { AccountPayer, FormValues } from "./types";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ReadOnlyTextField from "./FormComponents/ReadOnlyTextField";
import {
  capitalizeFirstChar,
  getSiteConditionsLabels,
  hasTotalLoadDetails,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
} from "./helper";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { format } from "date-fns";
import { usePlumberName } from "../Hooks/usePlumberName";
import { useSiteSupervisorName } from "../Hooks/useSiteSupervisorName";
import RequestorInfo from "./RequestorInfo";
import ReadOnlyUploadedFile from "./FormComponents/ReadOnlyUploadedFile";
import ReadOnlyContact from "./FormComponents/ReadOnlyContact";

const ReadOnlyGasLineBlock = () => {
  const { values } = useFormikContext<FormValues>();

  const {
    firstName: licensedPlumberFirstName,
    lastName: licensedPlumberLastName,
    email: licensedPlumberEmail,
    phone: licensedPlumberPhone,
    licenseNumber: licensedPlumberLicenseNumber,
  } = usePlumberName(
    values.gasLine?.licensedPlumber,
    values.requestingCompany?.msaNumber
  );

  const {
    firstName: siteSupervisorFirstName,
    lastName: siteSupervisorLastName,
    email: siteSupervisorEmail,
    phone: siteSupervisorPhone,
  } = useSiteSupervisorName(
    values.siteSupervisor,
    values.requestingCompany?.msaNumber
  );

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {/* Kraken related fields */}
        <ReadOnlyFieldWrapper
          value={values.gasLine.accountPayer === AccountPayer.COMPANY}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Account number"
              value={values.requestingCompany?.msaNumber || "Pending"}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Supply Service Order Number"
            value={values.gasLine?.supplyServiceOrderNumber || "Pending"}
          />
        </FormFieldLayout>
        {/* End Kraken related fields */}
        <ReadOnlyFieldWrapper value={values.gasLine?.buildingType}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="New or existing building"
              value={capitalizeFirstChar(values.gasLine?.buildingType)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.siteLockupDate}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Site lockup date"
              value={
                values.gasLine?.siteLockupDate
                  ? format(
                      new Date(values.gasLine?.siteLockupDate),
                      "dd/MM/yyyy"
                    )
                  : ""
              }
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.pressure}>
          <FormFieldLayout>
            <Columns collapseBelow="sm" space={{ xs: "xlarge", md: "medium" }}>
              <Column>
                <ReadOnlyTextField
                  label="Pressure"
                  value={
                    pressureOptions.find(
                      (option) => option.value === values.gasLine?.pressure
                    )?.label || values.gasLine?.pressure
                  }
                />
              </Column>
              {values.gasLine?.pressure === "0" ? (
                <Column>
                  <ReadOnlyTextField
                    label="Other pressure value (kPa)"
                    value={values.gasLine?.otherPressure}
                  />
                </Column>
              ) : null}
            </Columns>
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={
            values.gasLine.appliancesChecklistFiles &&
            values.gasLine.appliancesChecklistFiles.length > 0
          }
        >
          {values.gasLine.appliancesChecklistFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.totalLoad}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Total load, MJ/h"
              value={values.gasLine?.totalLoad || ""}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={hasTotalLoadDetails(values.gasLine?.totalLoadDetails)}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Appliances"
              value={populateTotalLoadSummary(values.gasLine?.totalLoadDetails)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={values.gasLine?.siteConditions?.length > 0}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Site conditions"
              value={getSiteConditionsLabels(values.gasLine?.siteConditions)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.otherSiteCondition}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other site condition"
              value={values.gasLine?.otherSiteCondition}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.meterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Meter location"
              value={
                meterLocationOptions.find(
                  (option) => option.value === values.gasLine?.meterLocation
                )?.label || values.gasLine?.meterLocation
              }
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasLine?.otherMeterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other meter location"
              value={values.gasLine?.otherMeterLocation}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
        {/* Contacts */}
        <ReadOnlyFieldWrapper value={licensedPlumberLicenseNumber}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasLineBlock.licensedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={licensedPlumberFirstName}
                lastName={licensedPlumberLastName}
                email={licensedPlumberEmail}
                phone={licensedPlumberPhone}
                licenceNumber={licensedPlumberLicenseNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper
          value={values.gasLine?.homeUserLicencedPlumber?.licenseNumber}
        >
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasLineBlock.homeUserLicencedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={values.gasLine?.homeUserLicencedPlumber?.firstName}
                lastName={values.gasLine?.homeUserLicencedPlumber?.lastName}
                email={values.gasLine?.homeUserLicencedPlumber?.email}
                phone={values.gasLine?.homeUserLicencedPlumber?.phoneNumber}
                licenceNumber={
                  values.gasLine?.homeUserLicencedPlumber?.licenseNumber
                }
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.siteSupervisor}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasLineBlock.siteSupervisor.column"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Site supervisor"
                firstName={siteSupervisorFirstName}
                lastName={siteSupervisorLastName}
                email={siteSupervisorEmail}
                phone={siteSupervisorPhone}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper value={values.gasLine.homeUserSiteSupervisor}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasLineBlock.homeUserSiteSupervisor.column"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Site supervisor"
                firstName={values.gasLine.homeUserSiteSupervisor?.firstName}
                lastName={values.gasLine.homeUserSiteSupervisor?.lastName}
                email={values.gasLine.homeUserSiteSupervisor?.email}
                phone={values.gasLine.homeUserSiteSupervisor?.phoneNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Contacts */}
        <ReadOnlyFieldWrapper
          value={
            values.gasLine.sitePlanFiles &&
            values.gasLine.sitePlanFiles.length > 0
          }
        >
          {values.gasLine.sitePlanFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper
          value={
            values.gasLine.shipperDocumentsFiles &&
            values.gasLine.shipperDocumentsFiles.length > 0
          }
        >
          {values.gasLine.shipperDocumentsFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <FormFieldLayout>
          <RequestorInfo
            requesterName={values.gasLine?.requesterName || ""}
            submittedDateTime={values.gasLine?.submittedDateTime || ""}
          />
        </FormFieldLayout>
      </Stack>
      {/* End Form container */}
    </Stack>
  );
};

export default ReadOnlyGasLineBlock;
